import type { Loader } from "jd-core-typescript-port";

import {
  parseClassFileMetadata,
  type ClassFileMetadata,
} from "./ClassFileMetadata.js";
import { ZipArchive, type ZipEntry } from "./ZipArchive.js";
import {
  ArchiveLoader,
  SingleClassLoader,
  type ArchiveClassEntry,
} from "../loaders/ArchiveLoader.js";
import { formatByteLength } from "../files/FileContent.js";
import type {
  ClassReference,
  FileReference,
  LoadedArtifact,
  TreeNode,
} from "../model/types.js";
import {
  appendRelativePath,
  classSourceRelativePath,
  normalizeRelativePath,
} from "../model/RelativePath.js";

interface MutableDirectory {
  readonly name: string;
  readonly path: string;
  readonly directories: Map<string, MutableDirectory>;
  readonly files: TreeNode[];
}

function fileName(path: string): string {
  const slash = path.lastIndexOf("/");
  return path.slice(slash + 1);
}

const ACC_BRIDGE = 0x0040;
const ACC_VARARGS = 0x0080;
const ACC_SYNTHETIC = 0x1000;

type ClassResolver = (internalName: string) => ClassReference | undefined;

function sourceSimpleName(internalName: string): string {
  const slash = internalName.lastIndexOf("/");
  const dollar = internalName.lastIndexOf("$");
  return internalName.slice(Math.max(slash, dollar) + 1);
}

function declarationSimpleName(metadata: ClassFileMetadata): string {
  const selfEntry = metadata.innerClasses.find(
    (entry) => entry.internalName === metadata.internalName,
  );
  return selfEntry?.simpleName ?? sourceSimpleName(metadata.internalName);
}

function visibleParameterTypes(
  metadata: ClassFileMetadata,
  descriptor: string,
  descriptorParameterTypes: readonly string[],
  accessFlags: number,
  constructor: boolean,
): readonly string[] {
  const parameterTypes = [...descriptorParameterTypes];

  if (constructor) {
    if (metadata.kind === "enum" && parameterTypes.length >= 2) {
      parameterTypes.splice(0, 2);
    }

    const selfEntry = metadata.innerClasses.find(
      (entry) => entry.internalName === metadata.internalName,
    );
    const enclosingInternalName =
      selfEntry?.outerInternalName ?? metadata.enclosingClassInternalName;
    if (
      enclosingInternalName !== null &&
      descriptor.startsWith(`(L${enclosingInternalName};`) &&
      parameterTypes.length > 0
    ) {
      parameterTypes.shift();
    }
  }

  if ((accessFlags & ACC_VARARGS) !== 0 && parameterTypes.length > 0) {
    const lastIndex = parameterTypes.length - 1;
    const lastType = parameterTypes[lastIndex]!;
    parameterTypes[lastIndex] = lastType.endsWith("[]")
      ? `${lastType.slice(0, -2)}...`
      : `${lastType}...`;
  }

  return parameterTypes;
}

function memberNodes(
  metadataReference: ClassReference,
  sourceReference: ClassReference,
  resolveClass: ClassResolver,
  parentRelativePath: string,
): readonly TreeNode[] {
  try {
    const metadata = metadataReference.loadMetadata();
    const nodes: TreeNode[] = [];
    const nestedInternalNames = new Set<string>();

    for (const innerClass of metadata.innerClasses) {
      if (
        innerClass.outerInternalName !== metadata.internalName ||
        innerClass.simpleName === null ||
        nestedInternalNames.has(innerClass.internalName)
      ) {
        continue;
      }

      const nestedReference = resolveClass(innerClass.internalName);
      if (nestedReference === undefined) {
        continue;
      }

      nestedInternalNames.add(innerClass.internalName);
      nodes.push(
        classNode(
          nestedReference,
          false,
          sourceReference,
          resolveClass,
          innerClass.simpleName,
          appendRelativePath(parentRelativePath, innerClass.simpleName),
        ),
      );
    }

    for (const [index, field] of metadata.fields.entries()) {
      if ((field.accessFlags & ACC_SYNTHETIC) !== 0) {
        continue;
      }
      const label = `${field.name} : ${field.type}`;
      nodes.push({
        id: `${metadataReference.id}:field:${index}:${field.name}`,
        label,
        kind: "field",
        relativePath: appendRelativePath(parentRelativePath, label),
        details: field.descriptor,
        classReference: sourceReference,
        sourceTarget: {
          kind: "field",
          internalName: metadata.internalName,
          name: field.name,
          descriptor: field.descriptor,
        },
      });
    }

    for (const [index, method] of metadata.methods.entries()) {
      if (
        method.name === "<clinit>" ||
        (method.accessFlags & (ACC_SYNTHETIC | ACC_BRIDGE)) !== 0
      ) {
        continue;
      }

      const constructor = method.name === "<init>";
      const parameterTypes = visibleParameterTypes(
        metadata,
        method.descriptor,
        method.parameterTypes,
        method.accessFlags,
        constructor,
      );
      if (constructor) {
        const label = `${declarationSimpleName(metadata)}(${parameterTypes.join(", ")})`;
        nodes.push({
          id: `${metadataReference.id}:constructor:${index}:${method.descriptor}`,
          label,
          kind: "constructor",
          relativePath: appendRelativePath(parentRelativePath, label),
          details: method.descriptor,
          classReference: sourceReference,
          sourceTarget: {
            kind: "constructor",
            internalName: metadata.internalName,
            descriptor: method.descriptor,
          },
        });
      } else {
        const label = `${method.name}(${parameterTypes.join(", ")}) : ${method.returnType}`;
        nodes.push({
          id: `${metadataReference.id}:method:${index}:${method.name}`,
          label,
          kind: "method",
          relativePath: appendRelativePath(parentRelativePath, label),
          details: method.descriptor,
          classReference: sourceReference,
          sourceTarget: {
            kind: "method",
            internalName: metadata.internalName,
            name: method.name,
            descriptor: method.descriptor,
          },
        });
      }
    }

    return nodes.sort(compareNodes);
  } catch (error) {
    return [
      {
        id: `${metadataReference.id}:metadata-error`,
        label: errorMessage(error),
        kind: "error",
        relativePath: appendRelativePath(
          parentRelativePath,
          errorMessage(error),
        ),
      },
    ];
  }
}

function classNode(
  metadataReference: ClassReference,
  defaultExpanded = false,
  sourceReference: ClassReference = metadataReference,
  resolveClass: ClassResolver = () => undefined,
  label: string = metadataReference.displayName,
  relativePath: string = appendRelativePath(
    classSourceRelativePath(sourceReference),
    label,
  ),
): TreeNode {
  return {
    id: `${metadataReference.id}:class-node:source:${sourceReference.id}`,
    label,
    kind: "class",
    relativePath,
    details: metadataReference.internalName,
    defaultExpanded,
    classReference: sourceReference,
    sourceTarget: {
      kind: "class",
      internalName: metadataReference.internalName,
    },
    loadChildren: async () =>
      memberNodes(
        metadataReference,
        sourceReference,
        resolveClass,
        relativePath,
      ),
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

class ArchiveClassReference implements ClassReference {
  private metadata: ClassFileMetadata | null = null;
  private readonly archiveLoader: ArchiveLoader;
  private readonly entry: ArchiveClassEntry;

  public readonly id: string;
  public readonly artifactId: string;
  public readonly entryPath: string;
  public readonly internalName: string;
  public readonly displayName: string;

  public constructor(
    artifactId: string,
    archiveLoader: ArchiveLoader,
    entry: ArchiveClassEntry,
  ) {
    this.artifactId = artifactId;
    this.archiveLoader = archiveLoader;
    this.entry = entry;
    this.entryPath = entry.path;
    this.internalName = entry.internalName;
    this.id = `${artifactId}:class:${entry.path}`;
    const slash = entry.internalName.lastIndexOf("/");
    this.displayName = entry.internalName
      .slice(slash + 1)
      .replaceAll("$", ".");
  }

  public createLoader(): Loader {
    return this.archiveLoader.scopedTo(this.entry);
  }

  public loadMetadata(): ClassFileMetadata {
    if (this.metadata === null) {
      this.metadata = parseClassFileMetadata(
        this.archiveLoader.loadEntry(this.entry),
      );
    }
    return this.metadata;
  }
}

class DirectClassReference implements ClassReference {
  private readonly bytes: Uint8Array;
  private readonly metadata: ClassFileMetadata;

  public readonly id: string;
  public readonly artifactId: string;
  public readonly entryPath: string;
  public readonly internalName: string;
  public readonly displayName: string;

  public constructor(
    artifactId: string,
    entryPath: string,
    bytes: Uint8Array,
    metadata: ClassFileMetadata,
  ) {
    this.artifactId = artifactId;
    this.entryPath = entryPath;
    this.bytes = bytes;
    this.metadata = metadata;
    this.internalName = metadata.internalName;
    this.displayName = metadata.simpleName;
    this.id = `${artifactId}:class:${entryPath}`;
  }

  public createLoader(): Loader {
    return new SingleClassLoader(this.internalName, this.bytes);
  }

  public loadMetadata(): ClassFileMetadata {
    return this.metadata;
  }
}

class ArchiveFileReference implements FileReference {
  private readonly archive: ZipArchive;

  public readonly id: string;
  public readonly artifactId: string;
  public readonly entryPath: string;
  public readonly displayName: string;
  public readonly byteLength: number;

  public constructor(
    artifactId: string,
    archive: ZipArchive,
    entry: ZipEntry,
  ) {
    this.archive = archive;
    this.artifactId = artifactId;
    this.entryPath = entry.name.replaceAll("\\", "/").replace(/^\/+/, "");
    this.displayName = fileName(this.entryPath);
    this.byteLength = entry.uncompressedSize;
    this.id = `${artifactId}:entry:${this.entryPath}`;
  }

  public readBytes(): Uint8Array {
    return this.archive.extract(this.entryPath);
  }
}

class DirectFileReference implements FileReference {
  private readonly bytes: Uint8Array;

  public readonly id: string;
  public readonly artifactId: string;
  public readonly entryPath: string;
  public readonly displayName: string;
  public readonly byteLength: number;

  public constructor(
    artifactId: string,
    name: string,
    bytes: Uint8Array,
  ) {
    this.artifactId = artifactId;
    this.entryPath = name;
    this.displayName = fileName(name);
    this.byteLength = bytes.length;
    this.bytes = bytes;
    this.id = `${artifactId}:entry:${name}`;
  }

  public readBytes(): Uint8Array {
    return this.bytes;
  }
}

function createMutableDirectory(name: string, path: string): MutableDirectory {
  return {
    name,
    path,
    directories: new Map(),
    files: [],
  };
}

function ensureDirectory(
  root: MutableDirectory,
  pathSegments: readonly string[],
): MutableDirectory {
  let current = root;
  let currentPath = "";

  for (const segment of pathSegments) {
    currentPath = currentPath.length === 0 ? segment : `${currentPath}/${segment}`;
    let child = current.directories.get(segment);
    if (child === undefined) {
      child = createMutableDirectory(segment, currentPath);
      current.directories.set(segment, child);
    }
    current = child;
  }

  return current;
}

function nodeRank(node: TreeNode): number {
  switch (node.kind) {
    case "directory":
      return 0;
    case "file":
      return 1;
    case "class":
      return 2;
    case "field":
      return 3;
    case "constructor":
      return 4;
    case "method":
      return 5;
    case "artifact":
      return 6;
    case "error":
      return 7;
  }
}

function compareNodes(left: TreeNode, right: TreeNode): number {
  const rankDifference = nodeRank(left) - nodeRank(right);
  if (rankDifference !== 0) {
    return rankDifference;
  }
  return left.label.localeCompare(right.label, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function isPackagePath(label: string): boolean {
  return label
    .split(".")
    .every(
      (segment) =>
        segment !== "classes" && /^[a-z_$][a-z\d_$]*$/u.test(segment),
    );
}

function compactDirectory(node: TreeNode): TreeNode {
  if (node.kind !== "directory") {
    return node;
  }

  let label = node.label;
  let relativePath = node.relativePath ?? node.label;
  let children: readonly TreeNode[] = (node.children ?? []).map(compactDirectory);

  while (
    children.length === 1 &&
    children[0]!.kind === "directory" &&
    isPackagePath(label) &&
    isPackagePath(children[0]!.label)
  ) {
    const child = children[0]!;
    label = `${label}.${child.label}`;
    relativePath = child.relativePath ?? relativePath;
    children = child.children ?? [];
  }

  return {
    ...node,
    label,
    relativePath,
    children,
  };
}

function finalizeDirectory(
  artifactId: string,
  directory: MutableDirectory,
): TreeNode {
  const children = [
    ...[...directory.directories.values()].map((child) =>
      finalizeDirectory(artifactId, child),
    ),
    ...directory.files,
  ].sort(compareNodes);

  return compactDirectory({
    id: `${artifactId}:directory:${directory.path}`,
    label: directory.name,
    kind: "directory",
    relativePath: normalizeRelativePath(directory.path),
    children,
  });
}

function createArchiveArtifact(
  artifactId: string,
  name: string,
  bytes: Uint8Array,
): LoadedArtifact {
  const archive = new ZipArchive(bytes);
  const archiveLoader = new ArchiveLoader(archive);
  const rootDirectory = createMutableDirectory("", "");
  const classes: ArchiveClassReference[] = [];
  const classReferencesByPath = new Map<string, ArchiveClassReference>();
  const classEntriesByPath = new Map<string, ArchiveClassEntry>();
  const classReferencesByInternalName = new Map<
    string,
    ArchiveClassReference[]
  >();

  for (const classEntry of archiveLoader.getClasses()) {
    const reference = new ArchiveClassReference(
      artifactId,
      archiveLoader,
      classEntry,
    );
    classes.push(reference);
    classReferencesByPath.set(classEntry.path, reference);
    classEntriesByPath.set(classEntry.path, classEntry);
    const candidates = classReferencesByInternalName.get(
      classEntry.internalName,
    );
    if (candidates === undefined) {
      classReferencesByInternalName.set(classEntry.internalName, [reference]);
    } else {
      candidates.push(reference);
    }
  }

  const resolveArchiveClass = (
    sourceVersion: number,
    internalName: string,
  ): ClassReference | undefined => {
    const candidates = classReferencesByInternalName.get(internalName);
    if (candidates === undefined || candidates.length === 0) {
      return undefined;
    }

    let selected: ArchiveClassReference | undefined;
    for (const candidate of candidates) {
      const candidateEntry = classEntriesByPath.get(candidate.entryPath)!;
      if (candidateEntry.version === sourceVersion) {
        selected = candidate;
      } else if (
        candidateEntry.version <= sourceVersion &&
        (selected === undefined ||
          classEntriesByPath.get(selected.entryPath)!.version <
            candidateEntry.version)
      ) {
        selected = candidate;
      }
    }

    if (selected !== undefined) {
      return selected;
    }

    return candidates.reduce((preferred, candidate) =>
      classEntriesByPath.get(candidate.entryPath)!.version >=
      classEntriesByPath.get(preferred.entryPath)!.version
        ? candidate
        : preferred,
    );
  };

  const nestedClassOwners = new Map<
    ArchiveClassReference,
    ArchiveClassReference
  >();
  for (const reference of classes) {
    if (!reference.internalName.includes("$")) {
      continue;
    }

    try {
      const metadata = reference.loadMetadata();
      const selfEntry = metadata.innerClasses.find(
        (entry) => entry.internalName === metadata.internalName,
      );
      const ownerInternalName =
        selfEntry?.outerInternalName ?? metadata.enclosingClassInternalName;
      if (ownerInternalName === null || ownerInternalName === undefined) {
        continue;
      }

      const sourceVersion = classEntriesByPath.get(reference.entryPath)!.version;
      const owner = resolveArchiveClass(sourceVersion, ownerInternalName);
      if (owner instanceof ArchiveClassReference && owner !== reference) {
        nestedClassOwners.set(reference, owner);
      }
    } catch {
      // Keep malformed or unsupported class files visible as independent files.
    }
  }

  const topLevelClasses = classes.filter(
    (reference) => !nestedClassOwners.has(reference),
  );
  const sourceReferenceFor = (
    reference: ArchiveClassReference,
  ): ArchiveClassReference => {
    const visited = new Set<ArchiveClassReference>();
    let current = reference;
    while (!visited.has(current)) {
      visited.add(current);
      const owner = nestedClassOwners.get(current);
      if (owner === undefined) {
        break;
      }
      current = owner;
    }
    return current;
  };

  for (const entry of archive.entries) {
    const normalizedPath = entry.name.replaceAll("\\", "/").replace(/^\/+/, "");
    const segments = normalizedPath.split("/").filter((segment) => segment.length > 0);

    if (segments.length === 0) {
      continue;
    }

    if (entry.directory) {
      ensureDirectory(rootDirectory, segments);
      continue;
    }

    const leafName = segments.at(-1)!;
    const parent = ensureDirectory(rootDirectory, segments.slice(0, -1));
    const fileReference = new ArchiveFileReference(artifactId, archive, entry);
    const classEntry = classEntriesByPath.get(normalizedPath);
    const children: TreeNode[] = [];
    const classReference = classReferencesByPath.get(normalizedPath);

    if (
      classReference !== undefined &&
      nestedClassOwners.has(classReference)
    ) {
      continue;
    }

    if (classReference !== undefined && classEntry !== undefined) {
      const resolveClass: ClassResolver = (internalName) =>
        resolveArchiveClass(classEntry.version, internalName);
      children.push(
        classNode(
          classReference,
          false,
          classReference,
          resolveClass,
        ),
      );
    }

    parent.files.push({
      id: `${fileReference.id}:node`,
      label: leafName,
      kind: "file",
      relativePath:
        classReference === undefined
          ? normalizeRelativePath(fileReference.entryPath)
          : classSourceRelativePath(classReference),
      details: `${formatByteLength(entry.uncompressedSize)} · ${normalizedPath}`,
      children,
      fileReference,
      ...(classReference === undefined
        ? {}
        : {
            classReference,
            sourceTarget: {
              kind: "class" as const,
              internalName: classReference.internalName,
            },
          }),
    });
  }

  const rootChildren = [
    ...[...rootDirectory.directories.values()].map((directory) =>
      finalizeDirectory(artifactId, directory),
    ),
    ...rootDirectory.files,
  ].sort(compareNodes);

  return {
    id: artifactId,
    name,
    byteLength: bytes.length,
    classCount: topLevelClasses.length,
    classes: topLevelClasses,
    resolveClassEntry(entryPath) {
      const normalizedPath = entryPath
        .replaceAll("\\", "/")
        .replace(/^\/+/, "");
      const metadataReference = classReferencesByPath.get(normalizedPath);
      if (metadataReference === undefined) {
        return null;
      }
      return {
        reference: sourceReferenceFor(metadataReference),
        target: {
          kind: "class",
          internalName: metadataReference.internalName,
        },
      };
    },
    root: {
      id: `${artifactId}:root`,
      label: name,
      kind: "artifact",
      relativePath: normalizeRelativePath(name),
      details: `${formatByteLength(bytes.length)} · ${topLevelClasses.length.toLocaleString()} classes`,
      defaultExpanded: true,
      children: rootChildren,
    },
  };
}

function createClassArtifact(
  artifactId: string,
  name: string,
  bytes: Uint8Array,
): LoadedArtifact {
  const metadata = parseClassFileMetadata(bytes);
  const reference = new DirectClassReference(
    artifactId,
    name,
    bytes,
    metadata,
  );

  return {
    id: artifactId,
    name,
    byteLength: bytes.length,
    classCount: 1,
    classes: [reference],
    resolveClassEntry(entryPath) {
      return entryPath === reference.entryPath
        ? {
            reference,
            target: {
              kind: "class",
              internalName: reference.internalName,
            },
          }
        : null;
    },
    root: {
      id: `${artifactId}:root`,
      label: name,
      kind: "artifact",
      relativePath: classSourceRelativePath(reference),
      details: `${formatByteLength(bytes.length)} · Java class ${metadata.majorVersion}.${metadata.minorVersion}`,
      defaultExpanded: true,
      children: [classNode(reference, true)],
      classReference: reference,
      sourceTarget: {
        kind: "class",
        internalName: reference.internalName,
      },
    },
  };
}

function createFileArtifact(
  artifactId: string,
  name: string,
  bytes: Uint8Array,
): LoadedArtifact {
  const reference = new DirectFileReference(artifactId, name, bytes);

  return {
    id: artifactId,
    name,
    byteLength: bytes.length,
    classCount: 0,
    classes: [],
    resolveClassEntry() {
      return null;
    },
    root: {
      id: `${artifactId}:root`,
      label: name,
      kind: "artifact",
      relativePath: normalizeRelativePath(reference.entryPath),
      details: formatByteLength(bytes.length),
      fileReference: reference,
    },
  };
}

export function createLoadedArtifact(
  artifactId: string,
  name: string,
  bytes: Uint8Array,
): LoadedArtifact {
  const extension = name.toLowerCase().split(".").at(-1) ?? "";

  switch (extension) {
    case "class":
      return createClassArtifact(artifactId, name, bytes);
    case "jar":
    case "zip":
    case "jmod":
      return createArchiveArtifact(artifactId, name, bytes);
    default:
      return createFileArtifact(artifactId, name, bytes);
  }
}
